import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import type { DropdownOption } from '@/components/common/ui/inputs/inputTypes'
import type { Country } from '@/types/external/2work/doctypes/generatorOutput/Geo/Country'
import type { IndustryType } from '@/types/external/2work/doctypes/generatorOutput/Selling/IndustryType'

import {
  type TranslatedTWPhoneType,
  type TranslatedTWRole,
  type TranslatedTWWeblinkType,
  useFrappeNestCustomAPIStore,
} from '@/stores/apis/frappeNestCustomAPI'
import { translationFunctionBackend } from '@/utilities/translations'

const getTranslatedLabel = (
  translatedLabel: string | undefined,
  originalLabel: string | undefined,
  fallbackLabel: string,
): string => {
  return translatedLabel ?? originalLabel ?? fallbackLabel
}

const useBackendConstantsStore = defineStore('BackendConstantsStore', () => {
  const customAPIStore = useFrappeNestCustomAPIStore()

  const tfBackend = translationFunctionBackend()

  /************************* WeblinkList *************************/
  const weblinkList = ref<TranslatedTWWeblinkType[]>([])
  const weblinkOptionList = computed((): DropdownOption<string>[] => {
    return weblinkList.value.map((weblinkItem) => {
      return {
        optionLabel: getTranslatedLabel(
          weblinkItem.translated_tw_weblink_type_name,
          weblinkItem.tw_weblink_type_name,
          weblinkItem.name,
        ),
        optionValue: weblinkItem.name,
      }
    })
  })
  const fetchWeblinkList = async (): Promise<void> => {
    weblinkList.value = await customAPIStore.getWebLinkTypes()
  }

  /************************* PhoneNumberList *************************/
  const phoneNumberList = ref<TranslatedTWPhoneType[]>([])

  const phoneNumberOptionList = computed((): DropdownOption<string>[] => {
    return phoneNumberList.value
      .map((phoneNumberItem) => {
        return {
          optionLabel: getTranslatedLabel(
            phoneNumberItem.translated_tw_phone_type_name,
            phoneNumberItem.tw_phone_type_name,
            phoneNumberItem.name,
          ),
          optionValue: phoneNumberItem.name,
        }
      })
      .sort((current, next) => current.optionLabel.localeCompare(next.optionLabel))
  })

  const fetchPhoneNumberList = async (): Promise<void> => {
    phoneNumberList.value = await customAPIStore.getPhoneTypes()
  }

  /************************* RoleList *************************/
  const roleList = ref<TranslatedTWRole[]>([])

  const roleOptionList = computed((): DropdownOption<string>[] => {
    return roleList.value.map((roleItem) => {
      return {
        optionLabel: getTranslatedLabel(
          roleItem.translated_tw_role_name,
          roleItem.tw_role_name,
          roleItem.name,
        ),
        optionValue: roleItem.name,
      }
    })
  })

  const fetchRoleList = async (): Promise<void> => {
    roleList.value = await customAPIStore.getRoles()
  }

  /************************* CountryList *************************/
  const countryList = ref<Country[]>([])
  const countryOptionList = computed((): DropdownOption<string>[] => {
    return countryList.value
      .map((countryItem) => {
        return {
          optionLabel: tfBackend(`countryNames.${countryItem.country_name}`),
          optionValue: countryItem.country_name,
        }
      })
      .sort((current, next) => current.optionLabel.localeCompare(next.optionLabel))
  })
  const fetchCountryList = async (): Promise<void> => {
    countryList.value = await customAPIStore.getCountries()
  }

  /************************* CustomerGroupList *************************/
  const customerGroupList = ref<{ customer_group_name: string; name: string }[]>([])
  const customerGroupOptionList = computed((): DropdownOption<null | string>[] => {
    return [
      {
        optionLabel: tfBackend('customerGroups.noneOptionLabel'),
        optionValue: null,
      },
      ...customerGroupList.value.map((item) => ({
        optionLabel: item.customer_group_name,
        optionValue: item.name,
      })),
    ]
  })
  const fetchCustomerGroupList = async (): Promise<void> => {
    customerGroupList.value = await customAPIStore.getCustomerGroupList()
  }

  /************************* SupplierGroupList *************************/
  const supplierGroupList = ref<{ name: string; supplier_group_name: string }[]>([])
  const supplierGroupOptionList = computed((): DropdownOption<null | string>[] => {
    return [
      {
        optionLabel: tfBackend('supplierGroups.noneOptionLabel'),
        optionValue: null,
      },
      ...supplierGroupList.value.map((item) => ({
        optionLabel: item.supplier_group_name,
        optionValue: item.name,
      })),
    ]
  })
  const fetchSupplierGroupList = async (): Promise<void> => {
    supplierGroupList.value = await customAPIStore.getSupplierGroupList()
  }

  /************************* IndustryList *************************/
  const industryList = ref<IndustryType[]>([])
  const industryOptionList = computed((): DropdownOption<null | string>[] => {
    const tempList = industryList.value
      .map((industryItem) => {
        return {
          optionLabel: tfBackend(`industryTypes.${industryItem.industry}`),
          optionValue: industryItem.name,
        }
      })
      .sort((current, next) => current.optionLabel.localeCompare(next.optionLabel))

    return [
      {
        isPlaceholderOption: true,
        optionLabel: tfBackend('industryTypes.noneOptionLabel'),
        optionValue: null,
      },
      ...tempList,
    ]
  })
  const fetchIndustryList = async (): Promise<void> => {
    industryList.value = await customAPIStore.getIndustryList()
  }

  /************************* SalutationList *************************/
  const salutationList = ref<{ name: string; title: string }[]>([])
  const salutationOptionList = computed((): DropdownOption<null | string>[] => {
    return [
      {
        isPlaceholderOption: true,
        optionLabel: tfBackend('salutations.noneOptionLabel'),
        optionValue: null,
      },
      ...salutationList.value
        .map((salutationItem) => ({
          optionLabel: salutationItem.title,
          optionValue: salutationItem.name,
        }))
        .sort((current, next) => current.optionLabel.localeCompare(next.optionLabel)),
    ]
  })
  const fetchSalutationList = async (): Promise<void> => {
    salutationList.value = await customAPIStore.getDoctypeItems('Salutation', undefined, 100, true)
  }

  return {
    countryOptionList,
    customerGroupOptionList,
    fetchCountryList,
    fetchCustomerGroupList,
    fetchIndustryList,
    fetchPhoneNumberList,
    fetchRoleList,
    fetchSalutationList,
    fetchSupplierGroupList,
    fetchWeblinkList,
    industryOptionList,
    phoneNumberList,
    phoneNumberOptionList,
    roleList,
    roleOptionList,
    salutationList,
    salutationOptionList,
    supplierGroupOptionList,
    weblinkList,
    weblinkOptionList,
  }
})

export { useBackendConstantsStore }
